import React, { useState, useRef, useEffect } from 'react';
import { Plus, Search, Filter, FilePlus, Layers, ChevronDown } from 'lucide-react';
import { getStatusBadgeColor, getPriorityBadgeColor, handlePriorityChange } from './dashboardUtils';
import StatusDropdown from './StatusDropdown';
import PriorityDropdown from './PriorityDropdown';
import CreateFromTemplateModal from './CreateFromTemplateModal';

const ProjectForm = ({ onSubmit, isSubmitting, onCancel = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'not_started',
    priority: 'medium'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    
    try {
      await onSubmit(formData);
      setFormData({ name: '', description: '', status: 'not_started', priority: 'medium' });
      if (onCancel) onCancel();
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-4 bg-slate-800 rounded-lg border border-slate-700">
      <input
        type="text"
        placeholder="Project name..."
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        required
      />
      <textarea
        placeholder="Description (optional)..."
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
        rows="2"
      />
      <select
        value={formData.priority}
        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
      >
        <option value="low">Low Priority</option>
        <option value="medium">Medium Priority</option>
        <option value="high">High Priority</option>
        <option value="critical">Critical Priority</option>
      </select>
      <div className="flex items-center space-x-2">
        <button
          type="submit"
          disabled={isSubmitting || !formData.name.trim()}
          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white px-3 py-2 rounded-lg transition-colors text-sm"
        >
          {isSubmitting ? 'Creating...' : 'Create Project'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 text-slate-400 hover:text-white border border-slate-600 hover:border-slate-500 rounded-lg transition-colors text-sm"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

const ProjectsList = ({
  projects = [],
  selectedProject,
  onSelectProject,
  onCreateProject,
  onUpdateProject,
  isSubmitting,
  hideCreateButton = false,
  title = "Projects",
  socket,
  showNotification,
  templates = [],
  onLoadTemplates,
  onCreateFromTemplate,
  onDeleteTemplate,
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showFromTemplate, setShowFromTemplate] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingProjects, setUpdatingProjects] = useState(new Set());
  const createMenuRef = useRef(null);

  useEffect(() => {
    if (!showCreateMenu) return;
    const handleClickOutside = (e) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target)) {
        setShowCreateMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreateMenu]);

  const templateSupportEnabled = typeof onCreateFromTemplate === 'function';

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    // Sort by status first (in_progress, on_hold, not_started)
    const statusOrder = { in_progress: 1, on_hold: 2, not_started: 3 };
    const aStatus = statusOrder[a.status] || 4;
    const bStatus = statusOrder[b.status] || 4;

    if (aStatus !== bStatus) {
      return aStatus - bStatus;
    }

    // Then sort by priority (critical, high, medium, low)
    const priorityOrder = { critical: 1, high: 2, medium: 3, low: 4 };
    const aPriority = priorityOrder[a.priority] || 5;
    const bPriority = priorityOrder[b.priority] || 5;

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // Finally sort by creation date (newest first)
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const handleCreateProject = async (formData) => {
    await onCreateProject(formData);
    setShowCreateForm(false);
  };

  const handleStatusChange = async (projectId, newStatus) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    
    setUpdatingProjects(prev => new Set(prev).add(projectId));
    try {
      await onUpdateProject(projectId, { 
        name: project.name,
        description: project.description || '',
        status: newStatus 
      });
    } finally {
      setUpdatingProjects(prev => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
    }
  };

  const handlePriorityChangeLocal = async (projectId, newPriority) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    
    setUpdatingProjects(prev => new Set(prev).add(projectId));
    try {
      await handlePriorityChange(
        project,
        newPriority,
        onUpdateProject,
        null, // setSelectedProject not needed here since parent handles it
        null, // setShowPriorityDropdown not needed
        showNotification,
        socket
      );
    } finally {
      setUpdatingProjects(prev => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
    }
  };

  return (
    <div className="w-80 bg-slate-800 border-r border-slate-700 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          {!hideCreateButton && (
            templateSupportEnabled ? (
              <div className="relative" ref={createMenuRef}>
                <button
                  onClick={() => setShowCreateMenu((v) => !v)}
                  className="flex items-center space-x-1 px-2 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  title="Create new project"
                >
                  <Plus className="w-4 h-4" />
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showCreateMenu && (
                  <div className="absolute right-0 mt-1 w-52 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-20">
                    <button
                      onClick={() => {
                        setShowCreateMenu(false);
                        setShowCreateForm(true);
                      }}
                      className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700 rounded-t-lg transition-colors"
                    >
                      <FilePlus className="w-4 h-4 text-blue-400" />
                      <span>Blank project</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateMenu(false);
                        setShowFromTemplate(true);
                      }}
                      className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700 rounded-b-lg transition-colors"
                    >
                      <Layers className="w-4 h-4 text-blue-400" />
                      <span>From template</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                title="Create new project"
              >
                <Plus className="w-4 h-4" />
              </button>
            )
          )}
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search projects..."
            className="w-full pl-9 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
          />
        </div>

        {/* Filter */}
        <div className="relative">
          <Filter className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm appearance-none"
          >
            <option value="all">All Status</option>
            <option value="not_started">Not Started</option>
            <option value="in_progress">In Progress</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && !hideCreateButton && (
        <div className="p-4 border-b border-slate-700">
          <ProjectForm
            onSubmit={handleCreateProject}
            isSubmitting={isSubmitting}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      )}

      {/* Create from Template Modal */}
      {templateSupportEnabled && (
        <CreateFromTemplateModal
          isOpen={showFromTemplate}
          onClose={() => setShowFromTemplate(false)}
          templates={templates}
          onLoadTemplates={onLoadTemplates}
          onCreate={onCreateFromTemplate}
          onDeleteTemplate={onDeleteTemplate}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Projects List */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        {filteredProjects.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
              <Plus className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-slate-400 text-sm">
              {searchTerm || filterStatus !== 'all' ? 'No projects match your search' : hideCreateButton ? 'No completed or cancelled projects yet' : 'No projects yet'}
            </p>
            {!searchTerm && filterStatus === 'all' && !hideCreateButton && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="mt-3 text-blue-400 hover:text-blue-300 text-sm"
              >
                Create your first project
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                onClick={() => onSelectProject(project)}
                className={`p-4 rounded-xl border cursor-pointer transition-all duration-300 group ${
                  selectedProject?.id === project.id
                    ? 'bg-blue-900/30 border-blue-500/50 shadow-lg shadow-blue-500/10 ring-1 ring-blue-500/20'
                    : 'bg-slate-800/50 border-slate-600/40 hover:border-slate-500/60 hover:bg-slate-800/70 hover:shadow-md hover:shadow-slate-900/20'
                }`}
              >
                {/* Top Row: Priority and Status */}
                <div className="flex items-center justify-between mb-2">
                  <PriorityDropdown
                    currentPriority={project.priority}
                    onPriorityChange={handlePriorityChangeLocal}
                    projectId={project.id}
                    size="sm"
                    isUpdating={updatingProjects.has(project.id)}
                  />
                  <StatusDropdown
                    currentStatus={project.status}
                    onStatusChange={handleStatusChange}
                    projectId={project.id}
                    size="sm"
                    isUpdating={updatingProjects.has(project.id)}
                  />
                </div>
                
                {/* Title */}
                <h4 className="font-semibold text-slate-100 text-base leading-tight mb-3 group-hover:text-white transition-colors">
                  {project.name}
                </h4>
                
                {/* Bottom Row: Username and Task Count */}
                <div className="flex items-center justify-between text-xs text-slate-500 pt-1 border-t border-slate-700/50">
                  <span className="font-medium text-slate-400">{project.created_by_name}</span>
                  <span className="text-slate-500">{project.total_tasks || 0} tasks</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsList;